/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

/** Common Components */
import FinalMolGrid from "../../../components/FinalMolGrid/FinalMolGrid";
import CardSection from "../../../components/ui/card/CardSection";

/** Utilities */
import { saveAs } from "file-saver";
import formatTime from "../../../lib/formatTime";
import formatDate from "../../../components/FinalMolGrid/libs/formatDate";

/** RTK Query */
import { useLazyExportTimesheetDataQuery, useLazyGetTimesheetListByFiltersQuery } from "../../../app/services/timeSheetLogAPI";

/** Common Services & Data Files */
import ToastService from "../../../services/toastService/ToastService";

/** Configuration Files */
import { TimesheetListGridConfig } from "./config/TimesheetList.data";
import TimesheetReportFilter from "./config/TimesheetReportFilter";

/** CSS Files */
import "./TimesheetReport.scss";

const TimesheetReport = () => {
    const molGridRef = useRef();
    const { user } = useSelector((state) => state.auth);
    const UserID = user.userId;

    // State variables for pagination, project, and user selection
    const [currentPage] = useState(1);
    const [pageSize] = useState(50);
    const [selectProject, setSelectProject] = useState(null);
    const [selectUser, setSelectUser] = useState(null);
    const [timesheetList, setTimesheetList] = useState([])
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();

    // API call
    const [getTimesheetFilterList, { isFetching: isFilterListFetching, isSuccess: isFilterList, data: FilterListData }] = useLazyGetTimesheetListByFiltersQuery();

    const [getDownloadFile, {
        isLoading: isExportFetching,
        isSuccess: isExportSucess,
        data: isExportData,
    },] = useLazyExportTimesheetDataQuery();

    useEffect(() => {
        if (!isExportFetching && isExportSucess && isExportData) {
            var file = new Blob([isExportData], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            let filename = `Timesheet Report ${formatDate(new Date(), "DD-MM-YYYY")}`;
            if (isExportData.size > 0) {
                saveAs(file, filename);
            }
            else {
                ToastService.error("No Record Found is Selected Date");
            }
        }
    }, [isExportFetching, isExportSucess, isExportData]);

    useEffect(() => {
        if (!isFilterListFetching && isFilterList && FilterListData) {
            if (FilterListData.dataSource) {
                const updtedData = FilterListData.dataSource.map(item => ({
                    ...item,
                    startTime: formatTime(item.startTime, "hh:mm A"),
                    endTime: formatTime(item.endTime, "hh:mm A"),
                    workingHours: formatWorkingHours(item.workingHours)
                }))
                setTimesheetList(updtedData)
            }
            if (FilterListData?.totalRecord) {
                setTotalRowCount(FilterListData?.totalRecord)
            }
        }
    }, [isFilterListFetching, isFilterList, FilterListData]);

    const formatWorkingHours = (hours) => {
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return `${h}h ${m}m`;
    };

    const handleDownload = (data) => {
        if (data?.userId === null || data === null) {
            return ToastService.warning("Please select any Users")
        }
        const exaldata = {
            projectId: data.projectId?.value,
            userId: data.userId?.value,
            startDate: data?.selectDate?.startDate ? moment(data?.selectDate?.startDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
            endDate: data?.selectDate?.endDate ? moment(data?.selectDate?.endDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
        };
        setStartDate(exaldata?.startDate);
        setEndDate(exaldata?.endDate);
        getDownloadFile(exaldata);
    };

    const handleGetRecord = (data) => {
        if (data?.userId === null || data === null) {
            return ToastService.warning("Please select any Users")
        }
        setSelectProject(data?.projectId?.value);
        setSelectUser(data.userId?.value);
        let request = {
            projectId: data.projectId?.value,
            userID: data.userId?.value,
            startDate: data?.selectDate?.startDate ? moment(data?.selectDate?.startDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
            endDate: data?.selectDate?.endDate ? moment(data?.selectDate?.endDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
            pagination: {
                pageNumber: currentPage,
                pageSize: pageSize,
            },
            filters: {
                searchText: ""
            },
            sortColumn: "",
            sortDirection: "",
        }
        setStartDate(request?.startDate);
        setEndDate(request.endDate);
        getTimesheetFilterList(request)
    }

    // Handle pagination changes
    const handlePageChange = (page) => {
        const newPage = page.pageNumber || page;
        const newPageSize = page.pageSize || pageSize;
        let request = {
            projectId: selectProject,
            userID: selectUser,
            startDate: startDate,
            endDate: endDate,
            pagination: {
                pageNumber: newPage,
                pageSize: newPageSize,
            },
            filters: {
                searchText: ""
            },
            sortColumn: "",
            sortDirection: "",
        }
        getTimesheetFilterList(request)
    };

    // Define action handlers for grid actions
    const actionHandler = {
        EDIT: "onEditAction",
        DELETE: "onDeleteClick",
    };

    return (
        <>
            <TimesheetReportFilter
                UserID={UserID}
                handleGetRecord={handleGetRecord}
                handleDownload={handleDownload}
            />
            <div className="report-list center-top-space">
                <CardSection
                    cardTitle="Timesheet List"
                    rightButton={false}
                    buttonClassName="theme-btn"
                    buttonText="Export"
                    textWithIcon={false}
                    iconName="material-symbols:add-rounded"
                    fontIcon={false}
                    searchInput={false}
                    isShorting={false}
                    customHeader={false}
                >
                    <FinalMolGrid
                        ref={molGridRef}
                        dataSource={timesheetList}
                        configuration={TimesheetListGridConfig}
                        onActionChange={actionHandler}
                        allowPagination={true}
                        pagination={{
                            totalCount: totalRowCount,
                            pageSize: pageSize,
                            currentPage: currentPage,
                        }}
                        onPageChange={handlePageChange}
                    />
                </CardSection>
            </div>
        </>
    );
};

export default TimesheetReport;